import styled, { css } from 'styled-components';
import { colors, fonts, radii } from '../../components/annuaire/designSystem';

/**
 * Les pièces communes du tunnel (lot 1, maquettes validées le 21-22/09 :
 * `docs/designs/fiche-annuaire-rdv-lot1-liste.md`). Jetons de l'annuaire ;
 * les couleurs de l'étape 2 reprennent celles de l'app mobile
 * (`rj-mobile/src/constants/colors.ts`), dont le sélecteur est repris.
 */

/** Au-dessus : fenêtre centrée par-dessus la fiche, colonne récap à droite. */
export const LARGE = '900px';

export const mobile = {
  muted: '#f0f4f8', // surfaceMuted
  highlight: '#f5ffdb', // primaryHighlight
  border: '#e2e8f0',
  mutedText: '#637d94',
};

const ICON_BASE = '/static/img/annuaire/icones/ROULEZJEUNESSE_ICONES__';
export const icone = (nom) => `${ICON_BASE}${nom}.png`;

export const Voile = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(49, 73, 89, 0.55);
`;

export const Fenetre = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1001;
  display: flex;
  background: ${colors.white};
  font-family: ${fonts.body};
  color: ${colors.textDark};

  @media (min-width: ${LARGE}) {
    inset: auto;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: min(980px, calc(100vw - 48px));
    height: min(700px, calc(100vh - 48px));
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  }
`;

export const Principal = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;

  @media (min-width: ${LARGE}) {
    border-right: 1px solid ${colors.borderLight};
  }
`;

export const Corps = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${(p) => (p.$flush ? '20px 0' : '20px 16px')};

  @media (min-width: ${LARGE}) {
    padding: ${(p) => (p.$flush ? '24px 0' : '24px 32px')};
  }
`;

export const Marge = styled.div`
  padding: 0 16px;

  @media (min-width: ${LARGE}) {
    padding: 0 32px;
  }
`;

export const Pied = styled.div`
  flex: 0 0 auto;
  padding: 10px 16px calc(14px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid ${mobile.border};
  background: ${colors.white};
  text-align: center;

  @media (min-width: ${LARGE}) {
    padding: 14px 32px 18px;
  }
`;

export const Titre = styled.h2`
  margin: 0 0 6px;
  font-family: ${fonts.heading};
  font-size: 22px;
  line-height: 1.15;
  color: ${colors.textDark};
  outline: none;
`;

export const SousTitre = styled.p`
  margin: 0 0 18px;
  font-size: 14px;
  line-height: 1.45;
  color: ${colors.textSecondary};
`;

export const BoutonVert = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 420px;
  min-height: 48px;
  margin: 0 auto;
  padding: 0 20px;
  border: none;
  border-radius: ${radii.button};
  background: ${colors.vert};
  color: ${colors.textDark};
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  text-decoration: none;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 3px solid ${colors.bleu};
    outline-offset: 2px;
  }
`;

export const BoutonLigne = styled(BoutonVert)`
  background: ${colors.white};
  border: 1.5px solid ${colors.borderLight};
`;

export const PetitTexte = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: ${colors.textSecondary};
`;

export const Compteur = styled.p`
  margin: 0 0 8px;
  font-size: 12.5px;
  color: ${mobile.mutedText};

  b {
    color: ${colors.textDark};
    font-weight: 600;
  }
`;

export const Encart = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  margin-bottom: 16px;
  border-radius: 14px;
  font-size: 13.5px;
  line-height: 1.45;

  ${(p) =>
    p.$ton === 'alerte'
      ? css`
          background: ${colors.orangePastel};
          color: #7a2e0c;
        `
      : css`
          background: ${colors.bleuPastel};
          color: #34409c;
        `}

  b {
    font-weight: 600;
  }
`;

export const Icone = styled.img`
  flex: 0 0 auto;
  width: ${(p) => p.$taille || 18}px;
  height: ${(p) => p.$taille || 18}px;
  object-fit: contain;
`;

/**
 * Où mène « Roulez Jeunesse » dans « Propulsé par Roulez Jeunesse » sur
 * `/reserver/:slug` : la page de Roulez Jeunesse pour les ateliers (à mettre à
 * jour côté contenu, l'adresse ne change pas).
 */
export const LIEN_PROPULSE =
  'https://www.roulezjeunesse.com/rejoindre-le-reseau-reparateur-velo?utm_source=reservation-atelier&utm_medium=propulse-par';

/** Le bandeau de la réservation instantanée (choix A du 21/09). */
export const TEXTE_INSTANTANE =
  'Cet atelier accepte les réservations en direct : choisissez votre horaire, votre rendez-vous est confirmé instantanément.';

/** Le même bandeau sur `/reserver/:slug` : l'horaire n'est confirmé qu'après le code. */
export const TEXTE_INSTANTANE_SITE =
  'Cet atelier confirme tout de suite : choisissez votre horaire, puis validez avec un code reçu par email.';

export const Bandeau = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px 16px;
  background: ${colors.vert};
  font-size: 13.5px;
  line-height: 1.4;

  b {
    font-weight: 600;
  }
`;
